import { Helmet } from "react-helmet"
import { toast } from "react-toastify"
import React, { useEffect, useState } from "react"
import { DialogTitle, MenuItem } from "@headlessui/react"

import { IDAM } from "../../api/Registry"
import { ErrorState } from "../errors/ErrorState"
import { APPLICATION } from "../../utils/Constants"
import DropDown from "../../lib/fragments/DropDown"
import { AddAuthProvider } from "./AddAuthProvider"
import HttpServices from "../../services/HttpServices"
import { AuthProviderConfig } from "./AuthProviderConfig"
import { EmptyResultSet } from "../errors/EmptyResultSet"
import { LoadingXL } from "../../components/utils/LoadingXL"
import { usePageContext } from "../../lib/guards/PageContext"
import { ConsentModal } from "../../lib/fragments/ConsentModal"
import { RouteHydration, classNames } from "../../utils/Helpers"
import { ModuleBreadCrumb } from "../../lib/fragments/ModuleBreadCrumb"
import authProviderImage from '../../resources/images/auth_provider.png'

export const AuthProvider = () => {
    const [state, setstate] = useState({
        httpStatus: 200,
        status: 'pending',
        data: {
            providers: null,
        },
        show: {
            uuid: null,
            addProvider: false,
            viewProvider: false,
            disableProvider: false,
        }
    })

    const pageTitle = "Auth Providers"
    type ModalKeys = 'addProvider' | 'disableProvider' | 'viewProvider' | ''

    const { updateDataContext } = usePageContext()
    const [actingState, setActingState] = useState(false)

    const breadCrumb = [
        { linkItem: false, title: 'IAM' },
        { linkItem: false, title: pageTitle },
    ]

    const fetchIdentityAuthProviders = async () => {
        let { status, httpStatus, data } = state

        try {
            const response: any = await HttpServices.httpGet(IDAM.PROVIDERS_IDX)
            httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload
                data.providers = payload.providers

                status = 'fulfilled'
                dataContextHandler(status)
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 0
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            loading: false,
            data: {
                ...prevState.data,
            },
        }))
    }

    useEffect(() => {
        dataContextHandler()
        fetchIdentityAuthProviders()
    }, [])

    const dataContextHandler = (status: string = 'pending') => {
        const baseContext = {
            title: pageTitle,
        }

        // Early return if data is not ready
        if (status !== 'fulfilled') {
            updateDataContext(baseContext)
            return
        }

        updateDataContext({
            ...baseContext,
            mainAction: {
                visible: true,
                disabled: false,
                icon: 'far fa-plus',
                label: 'New Auth Provider',
                onClick: () => toggleModal('addProvider', true),
                variant: 'primary' /* | 'secondary' | 'danger' | string */,
            }
        })
    }

    const toggleModal = (key: ModalKeys, value: boolean, uuid?: any) => {
        if (actingState) return

        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                [key]: value,
                uuid: uuid,
            },
        }))
    }

    const disableAuthProvider = async () => {
        if (actingState) return

        const { show } = state;
        setActingState(true);

        try {
            const route = [":slug"].reduce(
                (route, param, index) => RouteHydration(route, param, [show.uuid][index]),
                IDAM.PROVIDER_CONFIG
            );

            let formData = new FormData()
            formData.append('action', 'disable')

            const response: any = await HttpServices.httpPatch(route, formData);
            const success = response.status === 200;

            if (!success) toast.error(APPLICATION.ERR_MSG);
        } catch (error) {
            toast.error(APPLICATION.ERR_MSG);
        } finally {
            setActingState(false);
            toggleModal('disableProvider', false);
            fetchIdentityAuthProviders()
        }
    };

    const enableAuthProvider = async (provider: any) => {
        if (actingState) return

        try {
            const route = [":slug"].reduce(
                (route, param, index) => RouteHydration(route, param, [provider.s][index]),
                IDAM.PROVIDER_CONFIG
            );

            let formData = new FormData()
            formData.append('action', 'enable')

            const response: any = await HttpServices.httpPatch(route, formData);

            if (response.status === 200) {
                toast.success(`Auth provider ${provider.e} enabled`);
            } else {
                toast.error(APPLICATION.ERR_MSG);
            }
        } catch (error) {
            toast.error(APPLICATION.ERR_MSG);
        } finally {
            fetchIdentityAuthProviders()
        }
    }

    const makeAuthProviderDefault = async (provider: any) => {
        if (actingState) return
        if (provider.f === 'Y' || !provider.n) return

        try {
            const route = [":slug"].reduce(
                (route, param, index) => RouteHydration(route, param, [provider.s][index]),
                IDAM.PROVIDER_CONFIG
            );

            let formData = new FormData()
            formData.append('action', 'make_default')

            const response: any = await HttpServices.httpPatch(route, formData);

            if (response.status === 200) {
                toast.success(`Default authentication provider changed to ${provider.e}`);
            } else {
                toast.error(APPLICATION.ERR_MSG);
            }
        } catch (error) {
            toast.error(APPLICATION.ERR_MSG);
        } finally {
            fetchIdentityAuthProviders()
        }
    }

    const viewAuthProviderConfig = (provider: any) => {
        if (actingState) return
        if (!provider.n) return

        toggleModal('viewProvider', true, provider.s)
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <>
                            <div className="w-full py-2 border-b border-slate-200 dark:border-slate-600 lg:px-6 px-5 flex flex-col">
                                <div className="flex-none">
                                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                                </div>
                            </div>

                            <div className="md:h-full lg:h-full flex flex-col">
                                {
                                    state.data.providers.length < 1 ? (
                                        <div className="lg:px-6 px-5 flex-1 flex flex-col justify-center">
                                            <div className="md:hidden block pt-2">
                                                <div className="flex md:flex-row flex-col md:items-center gap-y-3 w-full">
                                                    <div className="flex-1 min-w-0 pt-1">
                                                        <h3 className="text-slate-900 dark:text-white text-xl md:text-2xl md:font-normal font-medium tracking-wide">
                                                            {pageTitle}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>

                                            <EmptyResultSet
                                                image={authProviderImage}
                                                description={
                                                    <div className="text-sm flex flex-col items-center gap-y-1">
                                                        <h3 className="text-slate-800 dark:text-slate-200 text-lg md:text-lg tracking-wide">
                                                            Configure a provider
                                                        </h3>

                                                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                                                            Authentication services, such as Google, Microsoft AD, that verify users identity before granting access into the system.
                                                        </p>

                                                        <div className="w-full pt-3">
                                                            <button type="button" onClick={() => toggleModal('addProvider', true)} className="w-auto min-w-28 disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-2 py-1.5 bg-orange-600 dark:bg-orange-600 text-white hover:bg-secondary dark:hover:bg-primary focus:outline-none">
                                                                Get Started
                                                            </button>
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="header-title align-middle title-transition lg:px-6 px-5 pt-3">
                                                <div className="md:hidden flex items-center align-middle pb-3">
                                                    <div className="flex-1">
                                                        <h3 className="text-slate-900 dark:text-white text-lg md:font-normal font-medium tracking-wide">
                                                            {pageTitle}
                                                        </h3>
                                                    </div>

                                                    <div className="flex-none flex flex-row-reverse">
                                                        <button type="button" onClick={() => toggleModal('addProvider', true)} className={`w-auto min-w-24 disabled:cursor-not-allowed text-sm rounded-md border border-transparent px-2 py-1.5 bg-inherit hover:bg-orange-50 text-orange-600 dark:hover:bg-orange-900/40 focus:outline-none flex items-center align-middle gap-x-2`}>
                                                            <span className="far fa-plus"></span>

                                                            <span className="text-sm">
                                                                Add Auth Provider
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>

                                                <p className="text-slate-600 dark:text-slate-300 md:pt-2 lg:pt-2 pb-4 text-sm md:w-[512px]">
                                                    Authentication services, such as Google, Microsoft AD, that verify users identity before granting access into the system.
                                                </p>

                                                <div className="w-full">
                                                    <div className="grid md:grid-cols-[minmax(160px,1fr)_70px_32px] gap-4 border-b border-slate-300 dark:border-slate-600 px-3 py-3 text-left text-sm text-slate-400 dark:text-slate-400 tracking-wider">
                                                        <span className="block">
                                                            Auth Provider
                                                        </span>

                                                        <span className="hidden md:block">
                                                            Status
                                                        </span>

                                                        <span className="hidden md:block"></span>
                                                    </div>

                                                    {
                                                        state.data.providers.map((provider: any) => (
                                                            <div key={`cnf-auth-prov-${provider.g}`} className="grid grid-cols-1 md:grid-cols-[minmax(160px,1fr)_70px_32px] gap-x-4 gap-y-1 px-2 py-3 border-b border-slate-100 dark:border-slate-700 text-sm">
                                                                <div className="text-slate-700 dark:text-slate-200 md:block flex align-middle md:items-center">
                                                                    <span className="flex-grow">
                                                                        {provider.e}

                                                                        {
                                                                            provider.f === 'Y' && (
                                                                                <span className="inline-flex bg-slate-50 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400 ring-slate-400 rounded-md px-1.5 py-0.5 text-xs ring-1 ring-inset capitalize ml-2">
                                                                                    Default
                                                                                </span>
                                                                            )
                                                                        }

                                                                        <span className="block mt-1 text-slate-500 dark:text-slate-400">
                                                                            {provider.t}
                                                                        </span>
                                                                    </span>

                                                                    <div className="flex-none md:hidden">
                                                                        <DropDown
                                                                            type={'borderless'}
                                                                            items={
                                                                                <div className="w-40">
                                                                                    <MenuItem>
                                                                                        <button type="button" /* onClick={() => handleProviderCatalog(catalog)} */
                                                                                            className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                                                            Edit Item
                                                                                        </button>
                                                                                    </MenuItem>

                                                                                    <MenuItem>
                                                                                        <button type="button" /* onClick={() => handleProviderCatalog(catalog)} */
                                                                                            className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                                                            Make Default
                                                                                        </button>
                                                                                    </MenuItem>

                                                                                    <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                                                                    <MenuItem>
                                                                                        <button type="button" /* onClick={() => handleProviderCatalog(catalog)} */
                                                                                            className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                                                            Disable
                                                                                        </button>
                                                                                    </MenuItem>
                                                                                </div>
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="flex flex-wrap align-middle items-center gap-4 md:contents w-full text-slate-500 dark:text-slate-400 pt-1">
                                                                    <div className="md:col-span-1 h-full flex flex-col justify-center">
                                                                        <span className={classNames(
                                                                            provider.n ? 'bg-purple-50 dark:bg-purple-800/10 text-purple-500 dark:text-purple-400 ring-purple-400' : 'bg-red-50 dark:bg-red-800/10 text-red-600 dark:text-red-400 ring-red-400',
                                                                            "rounded-md px-1.5 py-1 text-xs ring-1 ring-inset capitalize text-center"
                                                                        )}>
                                                                            {provider.n ? 'Enabled' : 'Disabled'}
                                                                        </span>
                                                                    </div>

                                                                    <div className="md:col-span-1 h-full flex flex-col justify-center">
                                                                        <div className="hidden md:block">
                                                                            <DropDown
                                                                                type={'borderless'}
                                                                                items={
                                                                                    <div className="w-40">
                                                                                        <MenuItem>
                                                                                            <button type="button" disabled={!provider.n || provider.s === 'local'} onClick={() => viewAuthProviderConfig(provider)}
                                                                                                className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 dark:disabled:data-[focus]:text-slate-600 disabled:text-slate-400 disabled:data-[focus]:bg-inherit dark:disabled:data-[focus]:bg-inherit hover:bg-slate-200 dark:hover:bg-slate-800 text-start dark:disabled:text-slate-600">
                                                                                                Provider Details
                                                                                            </button>
                                                                                        </MenuItem>

                                                                                        <MenuItem>
                                                                                            <button type="button" disabled={!provider.n || provider.f === 'Y'} onClick={() => makeAuthProviderDefault(provider)}
                                                                                                className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 dark:disabled:data-[focus]:text-slate-600 disabled:text-slate-400 disabled:data-[focus]:bg-inherit dark:disabled:data-[focus]:bg-inherit hover:bg-slate-200 dark:hover:bg-slate-800 text-start dark:disabled:text-slate-600">
                                                                                                Make Default
                                                                                            </button>
                                                                                        </MenuItem>

                                                                                        <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                                                                        {
                                                                                            provider.n ? (
                                                                                                <MenuItem>
                                                                                                    <button type="button" onClick={() => toggleModal('disableProvider', true, provider.s)}
                                                                                                        className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                                                                        Disable
                                                                                                    </button>
                                                                                                </MenuItem>
                                                                                            ) : (
                                                                                                <MenuItem>
                                                                                                    <button type="button" onClick={() => enableAuthProvider(provider)}
                                                                                                        className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                                                                        Enable
                                                                                                    </button>
                                                                                                </MenuItem>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        </>
                                    )
                                }
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center w-full overflow-auto py-4">
                            <LoadingXL />
                        </div>
                    )
                }

                <AddAuthProvider
                    show={state.show.addProvider}
                    reload={fetchIdentityAuthProviders}
                    showOrHide={() => toggleModal('addProvider', false)}
                />

                <ConsentModal
                    actionName={
                        actingState ? (
                            <span className="flex flex-row items-center h-6 justify-center gap-x-3">
                                <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                            </span>
                        ) : ('Disable')
                    }
                    show={state.show.disableProvider}
                    execAction={disableAuthProvider}
                    showOrHide={() => toggleModal('disableProvider', false)}
                    content={
                        <>
                            <DialogTitle as="h3" className="text-base leading-6 tracking-wide text-red-600 pb-2">
                                Disable Auth Provider?
                            </DialogTitle>

                            <div className="py-2">
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Disabling this provider will prevent users linked to it from signing in.
                                </p>
                            </div>
                        </>
                    }
                />

                <AuthProviderConfig
                    slug={state.show.uuid}
                    show={state.show.viewProvider}
                    reload={fetchIdentityAuthProviders}
                    showOrHide={() => toggleModal('viewProvider', false)}
                />
            </div>
        </React.Fragment>
    )
}